<template>
  <div class="column full-height menu-content">
    <q-scroll-area class="col q-pa-sm" visible style="height: calc(100% - 60px)">
      <div v-for="(group, index) in pageGroups" :key="index">
        <q-separator v-if="index !== 0" :color="$q.dark.isActive ? 'grey-14' : 'grey-13'" size="2px" />
        <q-list
          bordered
          separator
          :class="{
            'border-top-none': index !== 0,
            'border-bottom-none': index !== pageGroups.length - 1,
          }"
        >
          <template v-for="entry of group" :key="entry.key">
            <q-item
              v-if="entry.type === 'page'"
              v-ripple
              clickable
              :to="{ name: entry.page.name }"
              active-class="menu-active-item"
              :data-testid="`page-${entry.page.name}`"
            >
              <q-item-section avatar>
                <q-icon :name="entry.page.icon" />
              </q-item-section>

              <q-item-section>{{ t('menu.' + entry.page.name) }} </q-item-section>
            </q-item>

            <!-- The header is built from q-item-section rather than the icon/label props so it
                 renders the same markup as a plain menu item - the label prop would add a
                 q-item__label, which nothing else in the menu has. -->
            <q-expansion-item
              v-else
              :model-value="isSubGroupOpen(entry.subGroup, entry.pages)"
              :data-testid="`menu-group-${entry.subGroup}`"
              @update:model-value="setSubGroupOpen(entry.subGroup, $event)"
            >
              <template #header>
                <q-item-section avatar>
                  <q-icon :name="MENU_SUBGROUP_ICONS[entry.subGroup]" />
                </q-item-section>

                <q-item-section>{{ t('menu.' + entry.subGroup) }} </q-item-section>
              </template>

              <q-item
                v-for="page of entry.pages"
                :key="page.name"
                v-ripple
                clickable
                :inset-level="0.5"
                :to="{ name: page.name }"
                active-class="menu-active-item"
                :data-testid="`page-${page.name}`"
              >
                <q-item-section avatar>
                  <q-icon :name="page.icon" />
                </q-item-section>

                <q-item-section>{{ t('menu.' + page.name) }} </q-item-section>
              </q-item>
            </q-expansion-item>
          </template>
        </q-list>
      </div>
    </q-scroll-area>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { Page, usePage } from '@/composables/usePage';
import { MENU_SUBGROUP_ICONS, MENU_SUBGROUPS, PAGE_GROUPS, PAGE_NAMES } from '@/constants/pages';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import { useRoute } from 'vue-router';
import { getConfig } from '@/config';
import { useAuthStore } from '@/store/auth';
import { useAuthorization } from '@/composables/auth/useAuthorization';

type MenuEntry =
  | { type: 'page'; key: string; page: Page }
  | { type: 'subGroup'; key: string; subGroup: MENU_SUBGROUPS; pages: Page[] };

const { pages } = usePage();
const { t } = useI18n();
const authStore = useAuthStore();
const $q = useQuasar();
const route = useRoute();
const { hasLoggedUserPermission } = useAuthorization();

const currentUser = computed(() => authStore.currentAgwUser);

const tfOnlyPages = new Set<string>([
  PAGE_NAMES.DEV_METRICS,
  PAGE_NAMES.PCLDX_METRICS,
  PAGE_NAMES.METABASE_METRICS,
  PAGE_NAMES.PM_METRICS,
  PAGE_NAMES.DOCUMENTATION,
]);

const allowedPages = computed(() => {
  return Object.values(pages).filter((page) => {
    const isTfTenant = getConfig().TENANT == 'tf';
    if (tfOnlyPages.has(page.name) && !isTfTenant) {
      return false;
    }

    if (currentUser.value && page.permission.every((requiredPermission) => Permissions[requiredPermission.subject])) {
      return true;
    }

    const permissions = currentUser.value?.mappedPermissions;
    if (permissions) {
      return page.permission.length ? page.permission.some(hasLoggedUserPermission) : true;
    }

    return false;
  });
});

// Pages sharing a subGroup collapse into a single expansion item, which takes
// the menu position of its first page so the rest of the menu keeps its order.
const buildMenuEntries = (groupPages: Page[]): MenuEntry[] => {
  const entries: MenuEntry[] = [];
  const bySubGroup = new Map<MENU_SUBGROUPS, Extract<MenuEntry, { type: 'subGroup' }>>();

  for (const page of groupPages) {
    if (!page.subGroup) {
      entries.push({ type: 'page', key: page.name, page });
      continue;
    }

    const subGroupEntry = bySubGroup.get(page.subGroup);
    if (subGroupEntry) {
      subGroupEntry.pages.push(page);
      continue;
    }

    const entry: Extract<MenuEntry, { type: 'subGroup' }> = {
      type: 'subGroup',
      key: page.subGroup,
      subGroup: page.subGroup,
      pages: [page],
    };
    bySubGroup.set(page.subGroup, entry);
    entries.push(entry);
  }

  return entries;
};

const pageGroups = computed(() => {
  return Object.values(PAGE_GROUPS)
    .map((group) => buildMenuEntries(allowedPages.value.filter((page) => page.group === group)))
    .filter((entries) => entries.length);
});

// Until the user toggles a sub group by hand, it follows the active route.
const openedSubGroups = ref<Partial<Record<MENU_SUBGROUPS, boolean>>>({});

const isSubGroupOpen = (subGroup: MENU_SUBGROUPS, subGroupPages: Page[]): boolean => {
  return openedSubGroups.value[subGroup] ?? subGroupPages.some((page) => page.name === route.name);
};

const setSubGroupOpen = (subGroup: MENU_SUBGROUPS, opened: boolean): void => {
  openedSubGroups.value[subGroup] = opened;
};
</script>
<style lang="scss" scoped>
@import 'styles/app.variables.scss';

.menu-content {
  margin-left: -8px;
  margin-top: -8px;
}

.body--dark {
  .q-list .q-item .q-icon {
    color: nth($left-menu-icon-color, 1);
  }

  .q-list .q-item {
    color: nth($left-menu-text-color, 1);
  }

  .menu-active-item {
    color: nth($left-menu-text-color, 1) !important;
    background: nth($left-menu-background-hover-color, 1);
  }
}

.body--light {
  .q-list {
    background: white;
  }

  .q-list .q-item .q-icon {
    color: nth($left-menu-icon-color, 2);
  }

  .q-list .q-item {
    color: nth($left-menu-text-color, 2);
  }

  .menu-active-item {
    color: nth($left-menu-text-color, 2) !important;
    background: nth($left-menu-background-hover-color, 2);
  }
}

.border-top-none {
  border-top: none;
}

.border-bottom-none {
  border-bottom: none;
}

@media screen and (max-width: $bp720) {
  :deep(.q-icon) {
    font-size: $heading-medium-size;
  }

  :deep(.q-list .q-item) {
    font-size: $text-regular-size;
  }
}

@media screen and (min-width: ($bp720 + 1)) {
  :deep(.q-list .q-item) {
    font-size: $text-regular-size-small;
    height: $menu-item-small;
  }
}
</style>
